import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Stack,
    Typography,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import { captureToFile, getLiveViewFrame, probeCameraAgent } from "../utils/cameraAgent";

const STATUS_POLL_MS = 3000;
const FRAME_POLL_MS = 150; // ~6-7 fps: plenty for aligning a plate by hand

// Cloud-side panel for capturing measurement photos through the local
// Oraclase camera agent (browser bridge). Shows a live preview with an
// alignment overlay so the operator can place the plate in the camera's
// field of view, then captures with one click; the resulting raw file is
// handed to `onCaptured` exactly like a file picked from disk.
//
// Layout with an agent present: first row is the camera chip with the
// align-plate (live preview) toggle next to it, second row is the capture
// button with the manual-upload fallback next to it. Without a reachable
// agent only `manualUpload` is rendered, so the classic file-upload flow
// stays the only visible path on machines without a tethered camera.
//
// Props:
//   onCaptured: async (file: File) => void - receives the captured image.
//   disabled: disable the capture button (e.g. while a measurement runs).
//   label: short text on the capture button (default "Capture photo").
//   manualUpload: optional node with the section's manual file-upload
//     button; rendered next to the capture button (or alone when no agent
//     is reachable).
export default function CameraCapturePanel({
    onCaptured,
    disabled = false,
    label = "Capture photo",
    manualUpload = null,
}) {
    const [agentStatus, setAgentStatus] = useState(null); // null = still probing
    const [previewOpen, setPreviewOpen] = useState(false);
    const [frameUrl, setFrameUrl] = useState(null);
    const [capturing, setCapturing] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const frameUrlRef = useRef(null);
    const capturingRef = useRef(false);

    // Status poll: keeps the chips truthful about camera presence; also how
    // the panel appears/disappears when the agent is started or stopped.
    useEffect(() => {
        let cancelled = false;
        const poll = async () => {
            const status = await probeCameraAgent();
            if (!cancelled) setAgentStatus(status);
        };
        poll();
        const timer = setInterval(poll, STATUS_POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, []);

    const liveViewAvailable = Boolean(
        agentStatus && agentStatus.available && agentStatus.camera && agentStatus.camera.live_view_available
    );

    // Frame poll while the preview is open. Object URLs are swapped and the
    // previous one revoked so memory stays flat during long alignments.
    useEffect(() => {
        if (!previewOpen || !liveViewAvailable) return undefined;
        let cancelled = false;
        let timer = null;
        const pull = async () => {
            // Pause the preview during a capture: the camera switches from
            // live view to shooting and stale frames would just flicker.
            if (!capturingRef.current) {
                const blob = await getLiveViewFrame();
                if (cancelled) return;
                if (blob) {
                    const nextUrl = URL.createObjectURL(blob);
                    if (frameUrlRef.current) URL.revokeObjectURL(frameUrlRef.current);
                    frameUrlRef.current = nextUrl;
                    setFrameUrl(nextUrl);
                }
            }
            if (!cancelled) timer = setTimeout(pull, FRAME_POLL_MS);
        };
        pull();
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
            if (frameUrlRef.current) {
                URL.revokeObjectURL(frameUrlRef.current);
                frameUrlRef.current = null;
            }
            setFrameUrl(null);
        };
    }, [previewOpen, liveViewAvailable]);

    const handleCapture = useCallback(async () => {
        setErrorMessage(null);
        setCapturing(true);
        capturingRef.current = true;
        try {
            const file = await captureToFile();
            await onCaptured(file);
        } catch (error) {
            setErrorMessage(`Capture failed: ${error.message}`);
        } finally {
            capturingRef.current = false;
            setCapturing(false);
        }
    }, [onCaptured]);

    // No agent on this machine (or still probing): only the manual upload
    // path is shown.
    if (!agentStatus || !agentStatus.available) {
        if (!manualUpload) return null;
        return <Box sx={{ mt: 1.5, width: "100%" }}>{manualUpload}</Box>;
    }

    const camera = agentStatus.camera || {};
    const cameraConnected = Boolean(camera.connected);
    const remoteAllowed = Boolean(agentStatus.remote && agentStatus.remote.remote_capture_allowed);
    const isFolderWatch = agentStatus.backend === "folder_watch";

    let disabledReason = null;
    if (!cameraConnected) {
        disabledReason = "Connect the camera on this PC first (Camera card in Oraclase Communication).";
    } else if (!remoteAllowed) {
        disabledReason = "Enable remote captures in Oraclase Communication on this PC.";
    }

    return (
        <Box sx={{ mt: 1.5, width: "100%" }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                    size="small"
                    color={cameraConnected ? "success" : "default"}
                    label={cameraConnected ? `Camera: ${camera.model || "connected"}` : "No camera connected"}
                />
                {liveViewAvailable ? (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={previewOpen ? <VideocamOffIcon /> : <VideocamIcon />}
                        onClick={() => setPreviewOpen((open) => !open)}
                    >
                        {previewOpen ? "Hide preview" : "Align plate (preview)"}
                    </Button>
                ) : null}
            </Stack>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 1 }}
            >
                <Button
                    variant="contained"
                    size="small"
                    disableElevation
                    startIcon={
                        capturing ? <CircularProgress size={14} color="inherit" /> : <CameraAltIcon />
                    }
                    disabled={Boolean(disabledReason) || capturing || disabled}
                    onClick={handleCapture}
                >
                    {capturing
                        ? isFolderWatch
                            ? "Waiting for the shot…"
                            : "Capturing…"
                        : label}
                </Button>
                {manualUpload}
            </Stack>

            {disabledReason ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {disabledReason}
                </Typography>
            ) : null}
            {isFolderWatch && !disabledReason ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    Frame and release the shutter in Imaging Edge Remote — the photo is picked up
                    automatically.
                </Typography>
            ) : null}

            <Collapse in={previewOpen && liveViewAvailable} unmountOnExit>
                <Box
                    sx={{
                        mt: 1,
                        position: "relative",
                        width: "100%",
                        maxWidth: 560,
                        aspectRatio: "3 / 2",
                        borderRadius: 2,
                        overflow: "hidden",
                        bgcolor: "grey.900",
                        border: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    {frameUrl ? (
                        <img
                            src={frameUrl}
                            alt="Camera live view"
                            style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                        />
                    ) : (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <CircularProgress size={22} sx={{ color: "grey.500" }} />
                        </Box>
                    )}
                    {/* Alignment overlay: target zone + crosshair. Pure UI - the
                        measurement itself tolerates rough placement, this only
                        helps the operator keep the plate fully in frame. */}
                    <svg
                        viewBox="0 0 300 200"
                        preserveAspectRatio="none"
                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
                    >
                        <rect
                            x="45"
                            y="30"
                            width="210"
                            height="140"
                            fill="none"
                            stroke="rgba(80, 200, 120, 0.9)"
                            strokeWidth="1.2"
                            strokeDasharray="6 4"
                        />
                        <line x1="150" y1="92" x2="150" y2="108" stroke="rgba(80, 200, 120, 0.9)" strokeWidth="1" />
                        <line x1="142" y1="100" x2="158" y2="100" stroke="rgba(80, 200, 120, 0.9)" strokeWidth="1" />
                    </svg>
                    <Typography
                        variant="caption"
                        sx={{
                            position: "absolute",
                            left: 8,
                            bottom: 6,
                            color: "rgba(255,255,255,0.85)",
                            textShadow: "0 0 4px rgba(0,0,0,0.8)",
                        }}
                    >
                        Place the plate inside the dashed frame, then capture.
                    </Typography>
                </Box>
            </Collapse>

            {errorMessage ? (
                <Alert severity="error" sx={{ mt: 1, maxWidth: 560 }} onClose={() => setErrorMessage(null)}>
                    {errorMessage}
                </Alert>
            ) : null}
        </Box>
    );
}
